import type { WorkbookFreeformSheet } from "@erp/shared-types";
import type { SheetKindAdapter } from "../sheetKinds";
import { freeformTableColumns } from "./formula";
import { freeformReads, planFreeform } from "./plan";
import { EMPTY_LINEAGE } from "../lineage";
import { freeformTableLayout } from "./table";

/** A spreadsheet grid: each cell a literal or an Excel formula, its Result a declared range. */
export const freeformSheetKind: SheetKindAdapter<WorkbookFreeformSheet> = {
  input: () => undefined,
  reads: (sheet, tables) => freeformReads(sheet, tables),
  // A reference to a removed table is a Diagnostic of its cell (`planFreeform`).
  missingReads: () => [],
  resultColumns: (sheet) =>
    (freeformTableLayout(sheet)?.columns ?? []).map(
      ({ name, type, numberFormat }) =>
        numberFormat === undefined
          ? { name, type }
          : { name, type, numberFormat },
    ),
  dataNamedColumnType: () => undefined,
  plan: (sheet, { items, schemas }) => ({
    plan: {
      sheetId: sheet.id,
      output: (freeformTableLayout(sheet)?.columns ?? []).map(
        ({ name, type }) => ({ name, type }),
      ),
      stages: null,
    },
    diagnostics: planFreeform(sheet, items, schemas),
  }),
  // Every cell is Compute-relevant, and the table: its header row names columns.
  // A reference names another table's columns as stored, so renaming one
  // changes this Fingerprint only through that table's own.
  fingerprintInput: (sheet) => [
    null,
    [
      "freeform",
      sheet.cells,
      sheet.table?.range ?? null,
      sheet.table?.columns.map((column) => column.type) ?? [],
    ],
  ],
  // Cells name ranges, not columns: nothing to trace.
  lineage: () => EMPTY_LINEAGE,
  // A reference names its table's columns, so only those are read; a legacy
  // positional reference reads its table whole.
  columnReads: (sheet, { items }) =>
    [...freeformTableColumns(sheet, items)].map(([tableId, columns]) => ({
      tableId,
      columns,
    })),
  partName: (_, location) =>
    location.part === "cell" ? location.cell : undefined,
  mapFormulas: (sheet, { cell }) => ({
    ...sheet,
    cells: Object.fromEntries(
      Object.entries(sheet.cells).map(([address, input]) => [
        address,
        cell(input, address),
      ]),
    ),
  }),
  presentationFormats: (sheet) =>
    new Map(
      (freeformTableLayout(sheet)?.columns ?? []).map((column) => [
        column.name,
        column.numberFormat,
      ]),
    ),
};
